import React, { useState } from "react";
import { toast } from "sonner";
import { useApp } from "../contexts/AppContext";
import api, { formatApiError } from "../lib/api";
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from "lucide-react";

const Contact = () => {
    const { t } = useApp();
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            toast.success(t.contact.sent);
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="page-contact" className="container-x section-pad">
            <div className="max-w-3xl">
                <div className="chip bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))] mb-4">{t.nav.contact}</div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t.contact.title}</h1>
                <p className="mt-3 text-muted-foreground">{t.contact.desc}</p>
            </div>

            <div className="mt-12 grid lg:grid-cols-5 gap-8">
                <div className="lg:col-span-2 space-y-4">
                    <InfoCard icon={MapPin} title={t.contact.visit} body={"13-65/B1, Matha Ghar, MeeSeva Center, Near IOCL Petrol Pump, MBNR Main Road, Janampeta, Mahabubnagar, Telangana – 509334"} link="https://www.google.com/maps/place/MEESEVA+CENTER,+Sri+Matha+Multiservices,+HANWADA/@16.8078592,77.9235295,17z/data=!4m6!3m5!1s0x3bc98766fc235a95:0xa84dc579591df925!8m2!3d16.807853!4d77.9235249!16s%2Fg%2F11c2kx0_3g" linkLabel="Directions" />
                    <InfoCard icon={Phone} title={t.contact.call} body="+91 9000767404" link="tel:+919000767404" linkLabel="Call" />
                    <InfoCard icon={MessageCircle} title="WhatsApp" body="+91 9000767404" link="https://wa.me/919000767404" linkLabel="Chat" />
                    <InfoCard icon={Mail} title={t.contact.write} body="srimatha.multiservices@gmail.com" link="mailto:srimatha.multiservices@gmail.com" linkLabel="Send" />
                    <InfoCard icon={Clock} title={t.contact.hours} body={t.contact.hoursValue} />
                </div>

                <div className="lg:col-span-3">
                    <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-6 sm:p-8">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Input data-testid="contact-name" label={t.contact.name} value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                            <Input data-testid="contact-email" label={t.contact.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
                            <Input data-testid="contact-phone" label={t.contact.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                            <Input data-testid="contact-subject" label={t.contact.subject} value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
                            <div className="sm:col-span-2">
                                <label className="text-sm font-medium">{t.contact.message}<span className="text-destructive">*</span></label>
                                <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                                    className="mt-1.5 w-full rounded-xl border-2 border-border bg-background px-4 py-3 focus:border-[hsl(var(--royal))] focus:outline-none" />
                            </div>
                        </div>
                        <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-royal mt-6 disabled:opacity-40">
                            {loading ? "Sending…" : t.contact.send} <Send size={14} />
                        </button>
                    </form>
                </div>
            </div>

            <div className="mt-14 rounded-3xl overflow-hidden border border-border shadow-lg">
                <iframe
                    title="Sri Matha Multiservices Location"
                    src="https://maps.google.com/maps?q=16.807853,77.9235249&hl=en&z=17&output=embed"
                    width="100%" height="420" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                />
            </div>
        </div>
    );
};

const Input = ({ label, value, onChange, type = "text", required, ...rest }) => (
    <div>
        <label className="text-sm font-medium">{label}{required && <span className="text-destructive">*</span>}</label>
        <input {...rest} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
            className="mt-1.5 w-full rounded-xl border-2 border-border bg-background px-4 py-3 focus:border-[hsl(var(--royal))] focus:outline-none" />
    </div>
);

const InfoCard = ({ icon: Icon, title, body, link, linkLabel }) => (
    <div className="p-5 rounded-2xl border border-border bg-card">
        <div className="flex gap-4">
            <div className="w-11 h-11 rounded-xl bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))] flex items-center justify-center shrink-0"><Icon size={18} /></div>
            <div className="flex-1 min-w-0">
                <div className="font-semibold">{title}</div>
                <div className="mt-1 text-sm text-muted-foreground whitespace-pre-line break-words">{body}</div>
                {link && <a href={link} target={link.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="mt-2 inline-block text-xs font-semibold text-[hsl(var(--royal))] hover:underline">{linkLabel} →</a>}
            </div>
        </div>
    </div>
);

export default Contact;
